import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";

export default function FarmerLogin() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { requestOtp, verifyOtp } = useAuth();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [requestId, setRequestId] = useState("");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);

  const submitPhone = async (e) => {
    e.preventDefault();
    const clean = phone.replace(/\D/g, "").slice(-10);
    if (clean.length !== 10) { toast.error(t("auth.invalid_phone")); return; }
    setBusy(true);
    try {
      const r = await requestOtp(clean);
      setRequestId(r.request_id);
      if (r.dev_hint) setHint(r.dev_hint);
      setStep(2);
      toast.success(t("auth.otp_sent"));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error || t("toast.generic_error"));
    } finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error(t("auth.invalid_otp")); return; }
    setBusy(true);
    try {
      await verifyOtp(requestId, otp.trim());
      toast.success(t("auth.welcome"));
      nav("/farmer");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error || t("auth.invalid_otp"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-farmer-login" className="max-w-md mx-auto px-6 py-16">
      <div className="tr-eyebrow">{t("auth.eyebrow")}</div>
      <h1 className="tr-section-title text-3xl lg:text-4xl mt-4">{t("auth.farmer_title")}</h1>
      <p className="mt-3 text-[var(--tr-muted)]">{t("auth.farmer_body")}</p>

      {step === 1 ? (
        <form onSubmit={submitPhone} className="tr-card p-6 mt-8 space-y-5" data-testid="farmer-login-phone-form">
          <label className="block">
            <span className="tr-label">{t("auth.mobile_number")}*</span>
            <input required inputMode="tel" data-testid="farmer-login-phone" className="tr-input text-xl tracking-widest" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" />
          </label>
          <button type="submit" disabled={busy} data-testid="farmer-login-send-otp" className="tr-pill-btn tr-btn-primary w-full disabled:opacity-60">
            {busy ? t("common.sending") : t("auth.send_otp")} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      ) : (
        <form onSubmit={submitOtp} className="tr-card p-6 mt-8 space-y-5" data-testid="farmer-login-otp-form">
          <div className="text-sm text-[var(--tr-muted)]">{t("auth.otp_prompt", { phone })}</div>
          <label className="block">
            <span className="tr-label">{t("auth.otp_code")}*</span>
            <input required inputMode="numeric" maxLength={6} data-testid="farmer-login-otp" className="tr-input text-2xl tracking-[0.5em] text-center" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} />
          </label>
          {hint && <div data-testid="farmer-login-devhint" className="text-xs text-[var(--tr-zest)] bg-[var(--tr-citrus-yellow)]/10 border border-[var(--tr-citrus-yellow)]/30 rounded-xl p-3">{hint}</div>}
          <button type="submit" disabled={busy} data-testid="farmer-login-submit" className="tr-pill-btn tr-btn-primary w-full disabled:opacity-60">
            {busy ? t("common.submitting") : t("auth.verify")} <ArrowRight className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => { setStep(1); setOtp(""); setHint(""); }} className="text-sm text-[var(--tr-muted)] w-full text-center underline">
            {t("auth.change_number")}
          </button>
        </form>
      )}
    </div>
  );
}
